import { useEffect, useRef, useState } from 'react';

/**
 * useGeolocation — single-shot browser geolocation.
 *
 * We only prompt the user once per page mount and never re-request
 * on rerender. If the browser has no geolocation support, the
 * caller is denied, or the request errors/times out, we resolve to
 * `{ denied: true }` and the page continues to work without a
 * distance sort. Coordinates are held in component state only —
 * revisiting the page will re-ask on browsers that don't have a
 * lingering permission decision.
 */
export default function useGeolocation() {
  const [state, setState] = useState({
    coords: null,
    resolving: true,
    denied: false,
    unsupported: false,
  });
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setState({ coords: null, resolving: false, denied: false, unsupported: true });
      return;
    }

    let done = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (done) return;
        done = true;
        setState({
          coords: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          resolving: false,
          denied: false,
          unsupported: false,
        });
      },
      () => {
        if (done) return;
        done = true;
        setState({ coords: null, resolving: false, denied: true, unsupported: false });
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60_000 },
    );
  }, []);

  return state;
}
